import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './api.js';
import { Layout } from './components/Layout.js';
import { Activity } from './pages/Activity.js';
import { Assistant } from './pages/Assistant.js';
import { Audit } from './pages/Audit.js';
import { BookValue } from './pages/BookValue.js';
import { CarrierSetup } from './pages/CarrierSetup.js';
import { ChargebackProof } from './pages/ChargebackProof.js';
import { Chargebacks } from './pages/Chargebacks.js';
import { Compare } from './pages/Compare.js';
import { Dashboard } from './pages/Dashboard.js';
import { Disputes } from './pages/Disputes.js';
import { Docs } from './pages/Docs.js';
import { Files } from './pages/Files.js';
import { JobResults } from './pages/JobResults.js';
import { Jobs } from './pages/Jobs.js';
import { Journey } from './pages/Journey.js';
import { Login } from './pages/Login.js';
import { MedicareRde } from './pages/MedicareRde.js';
import { NotFound } from './pages/NotFound.js';
import { OAuthCallback } from './pages/OAuthCallback.js';
import { PaymentGaps } from './pages/PaymentGaps.js';
import { Profile } from './pages/Profile.js';
import { Rates } from './pages/Rates.js';
import { Register } from './pages/Register.js';
import { TaxReconciliation } from './pages/TaxReconciliation.js';
import { UnpaidMembers } from './pages/UnpaidMembers.js';
import { Workspaces } from './pages/Workspaces.js';

/** Full-screen notice for an account an operator has disabled: access is
 * blocked everywhere with the same message, and the data is preserved. */
function AccountDisabledScreen() {
  const { signOut } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-6 dark:bg-neutral-950">
      <div className="w-full max-w-md rounded-2xl border border-rose-200 bg-white p-6 text-center shadow-sm dark:border-rose-900 dark:bg-neutral-900">
        <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          This account has been disabled
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
          Access has been turned off by CommissionSight. Your data is preserved and nothing has been
          deleted. Contact{' '}
          <a className="font-medium text-emerald-600" href="mailto:support@commissionsight.com">
            support@commissionsight.com
          </a>{' '}
          to restore access.
        </p>
        <button
          type="button"
          onClick={signOut}
          className="mt-5 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

export function App() {
  const { token, accountDisabled } = useAuth();

  if (!token) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (accountDisabled) {
    return <AccountDisabledScreen />;
  }

  return (
    <Routes>
      {/* Already signed in: the marketing CTAs point at /register and /login —
          bounce those to the dashboard instead of falling through to NotFound. */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/register" element={<Navigate to="/" replace />} />
      <Route path="/auth/callback" element={<Navigate to="/" replace />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/files" element={<Files />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/jobs/:jobId" element={<JobResults />} />
        <Route path="/members/:memberRefId" element={<Journey />} />
        <Route path="/policies/:policyRefId" element={<Journey />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/chargebacks" element={<Chargebacks />} />
        <Route path="/chargebacks/proof" element={<ChargebackProof />} />
        <Route path="/chargebacks/gaps" element={<PaymentGaps />} />
        <Route path="/medicare" element={<MedicareRde />} />
        <Route path="/book-value" element={<BookValue />} />
        <Route path="/unpaid" element={<UnpaidMembers />} />
        <Route path="/tax-1099" element={<TaxReconciliation />} />
        <Route path="/disputes" element={<Disputes />} />
        <Route path="/rates" element={<Rates />} />
        <Route path="/carrier-setup" element={<CarrierSetup />} />
        <Route path="/ask" element={<Assistant />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/docs/:slug" element={<Docs />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/workspaces" element={<Workspaces />} />
        <Route path="/activity" element={<Activity />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
